import React from 'react';
import { Card, Input, Select, Segmented, Space, Tag, theme } from 'antd';
import { AppstoreOutlined, ClearOutlined, SearchOutlined, UnorderedListOutlined } from '@ant-design/icons';
import type { SalesTeam } from '@/services/salesTeam.service';
import { LEAD_PRIORITIES } from './leads.constants';
import type { LeadsAssignScope, LeadsViewMode } from '@/hooks/useLeadsBoardFilters';

const { Search } = Input;

type Props = {
  searchInput: string;
  onSearchChange: (v: string) => void;
  priorityFilter: string;
  onPriorityChange: (v: string) => void;
  assignScope: LeadsAssignScope;
  onAssignScopeChange: (v: LeadsAssignScope) => void;
  teamFilter: string;
  missingTeamOnly: boolean;
  onTeamChange: (teamId: string | undefined, missingOnly: boolean) => void;
  salesTeams: SalesTeam[];
  viewMode: LeadsViewMode;
  onViewModeChange: (v: LeadsViewMode) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
};

export const LeadsFilterBar: React.FC<Props> = ({
  searchInput,
  onSearchChange,
  priorityFilter,
  onPriorityChange,
  assignScope,
  onAssignScopeChange,
  teamFilter,
  missingTeamOnly,
  onTeamChange,
  salesTeams,
  viewMode,
  onViewModeChange,
  hasActiveFilters,
  onClearFilters,
}) => {
  const { token } = theme.useToken();

  return (
    <Card
      style={{
        marginBottom: 16,
        borderRadius: token.borderRadiusLG,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <Segmented
            value={viewMode}
            onChange={(v) => onViewModeChange(v as LeadsViewMode)}
            options={[
              { label: 'Kanban', value: 'kanban', icon: <AppstoreOutlined /> },
              { label: 'Table', value: 'table', icon: <UnorderedListOutlined /> },
            ]}
          />
          {hasActiveFilters && (
            <Tag
              icon={<ClearOutlined />}
              style={{ cursor: 'pointer', margin: 0 }}
              onClick={onClearFilters}
            >
              Clear filters
            </Tag>
          )}
        </div>
        <Space wrap style={{ width: '100%' }}>
          <Search
            placeholder="Search leads…"
            allowClear
            style={{ width: 280 }}
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <Select
            allowClear
            placeholder="Priority"
            style={{ width: 140 }}
            value={priorityFilter || undefined}
            onChange={(v) => onPriorityChange(v || '')}
            options={LEAD_PRIORITIES.map((p) => ({ label: p, value: p }))}
          />
          <Select
            style={{ width: 160 }}
            value={assignScope}
            onChange={(v) => onAssignScopeChange(v)}
            options={[
              { label: 'All owners', value: 'all' },
              { label: 'My leads', value: 'mine' },
              { label: 'Unassigned', value: 'unassigned' },
            ]}
          />
          <Select
            allowClear
            placeholder="Sales team"
            style={{ width: 200 }}
            value={missingTeamOnly ? '__missing__' : teamFilter || undefined}
            onChange={(v) => {
              if (v === '__missing__') onTeamChange(undefined, true);
              else onTeamChange(v || undefined, false);
            }}
            options={[
              { label: 'Missing / unassigned team', value: '__missing__' },
              ...salesTeams.map((t) => ({ label: t.team_name, value: t.id })),
            ]}
          />
        </Space>
      </Space>
    </Card>
  );
};
